import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { metaPixelService } from '../lib/metaPixel';

export const useMetaPixel = () => {
  const location = useLocation();

  // Initialize UTM tracking and track page views on route changes
  useEffect(() => {
    if (metaPixelService.isPixelEnabled()) {
      metaPixelService.initializeUTMTracking();
      metaPixelService.trackEvent({
        eventName: 'PageView',
        parameters: {
          page_name: location.pathname,
          page_url: window.location.href
        }
      });
    }
  }, [location]);

  const trackEvent = useCallback((eventName: string, parameters?: Record<string, any>, value?: number, currency?: string) => {
    if (metaPixelService.isPixelEnabled()) {
      metaPixelService.trackEvent({
        eventName,
        parameters,
        value,
        currency
      });
    }
  }, []);

  const trackPopupBlocked = useCallback((authMethod: 'google' | 'jobprot', location: string, additionalParams?: Record<string, any>) => {
    if (metaPixelService.isPixelEnabled()) {
      metaPixelService.trackPopupBlocked(authMethod, location, additionalParams);
    }
  }, []);

  const trackAuthAttempt = useCallback((authMethod: 'google' | 'jobprot', location: string, additionalParams?: Record<string, any>) => {
    if (metaPixelService.isPixelEnabled()) {
      metaPixelService.trackAuthAttempt(authMethod, location, additionalParams);
    }
  }, []);

  const trackAuthSuccess = useCallback((authMethod: 'google' | 'jobprot', location: string, additionalParams?: Record<string, any>) => {
    if (metaPixelService.isPixelEnabled()) {
      metaPixelService.trackAuthSuccess(authMethod, location, additionalParams);
    }
  }, []);

  const trackAuthFailure = useCallback((authMethod: 'google' | 'jobprot', location: string, errorType: string, errorMessage: string, additionalParams?: Record<string, any>) => {
    if (metaPixelService.isPixelEnabled()) {
      metaPixelService.trackAuthFailure(authMethod, location, errorType, errorMessage, additionalParams);
    }
  }, []);

  const trackButtonClick = useCallback((buttonName: string, location: string) => {
    if (metaPixelService.isPixelEnabled()) {
      metaPixelService.trackEvent({
        eventName: 'CustomEvent',
        parameters: {
          event_name: 'ButtonClick',
          button_name: buttonName,
          button_location: location
        }
      });
    }
  }, []);

  const trackFormSubmission = useCallback((formName: string, success: boolean) => {
    if (metaPixelService.isPixelEnabled()) {
      metaPixelService.trackEvent({
        eventName: 'CustomEvent',
        parameters: {
          event_name: 'FormSubmission',
          form_name: formName,
          success: success
        }
      });
    }
  }, []);

  const trackVideoInteraction = useCallback((action: 'play' | 'pause' | 'complete', videoName: string) => {
    if (metaPixelService.isPixelEnabled()) {
      metaPixelService.trackEvent({
        eventName: 'CustomEvent',
        parameters: {
          event_name: 'VideoInteraction',
          video_action: action,
          video_name: videoName
        }
      });
    }
  }, []);

  const trackScrollDepth = useCallback((depth: number) => {
    if (metaPixelService.isPixelEnabled()) {
      metaPixelService.trackEvent({
        eventName: 'CustomEvent',
        parameters: {
          event_name: 'ScrollDepth',
          scroll_depth: depth
        }
      });
    }
  }, []);

  const trackTimeOnPage = useCallback((seconds: number) => {
    if (metaPixelService.isPixelEnabled()) {
      metaPixelService.trackEvent({
        eventName: 'CustomEvent',
        parameters: {
          event_name: 'TimeOnPage',
          time_on_page: seconds
        }
      });
    }
  }, []);

  const trackPricingView = useCallback(() => {
    if (metaPixelService.isPixelEnabled()) {
      metaPixelService.trackEvent({
        eventName: 'ViewContent',
        parameters: {
          content_name: 'Pricing Page',
          content_category: 'pricing'
        }
      });
    }
  }, []);

  const trackLead = useCallback((value?: number, currency?: string) => {
    if (metaPixelService.isPixelEnabled()) {
      metaPixelService.trackEvent({
        eventName: 'Lead',
        parameters: {
          content_name: 'Lead Generation',
          content_category: 'lead'
        },
        value,
        currency
      });
    }
  }, []);

  const trackSignUp = useCallback((value?: number, currency?: string) => {
    if (metaPixelService.isPixelEnabled()) {
      metaPixelService.trackEvent({
        eventName: 'CompleteRegistration',
        parameters: {
          content_name: 'User Registration',
          content_category: 'registration'
        },
        value,
        currency
      });
    }
  }, []);

  const trackStartTrial = useCallback((value?: number, currency?: string) => {
    if (metaPixelService.isPixelEnabled()) {
      metaPixelService.trackEvent({
        eventName: 'InitiateCheckout',
        parameters: {
          content_name: 'Trial Start',
          content_category: 'trial'
        },
        value,
        currency
      });
    }
  }, []);

  return {
    trackEvent,
    trackPopupBlocked,
    trackAuthAttempt,
    trackAuthSuccess,
    trackAuthFailure,
    trackButtonClick,
    trackFormSubmission,
    trackVideoInteraction,
    trackScrollDepth,
    trackTimeOnPage,
    trackPricingView,
    trackLead,
    trackSignUp,
    trackStartTrial,
    isPixelEnabled: metaPixelService.isPixelEnabled()
  };
}; 